import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Button, Table, Upload, Modal, Form, Input, Select, Space,
  Popconfirm, message, Empty, Alert, Typography,
} from 'antd';
import { UploadOutlined, PlusOutlined, DeleteOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getCompetitionDocuments, uploadCompetitionDocument, deleteDocument, getDocumentTypes,
  getCompetitionNotes, createCompetitionNote, updateCompetitionNote, deleteCompetitionNote,
} from '../../api/competitions';
import { textSorter, dateSorter } from '../../utils/tableSorters';
import useAuthStore from '../../store/authStore';
import { apiErrorMessage } from '../../utils/apiError';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const fileSize = (b) => {
  if (!b) return '—';
  const mb = b / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
};

/**
 * Yarışma geneli belgeler — etap tanımlanmadan da kullanılabilir.
 * Liste tamamı tek seferde geliyor (sayfalama yok), bu yüzden istemci
 * sıralaması doğru sonuç verir (bkz. CLAUDE.md sıralama kuralı).
 */
export const CompetitionDocumentsTab = ({ competitionId }) => {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCompetitionDocuments(competitionId);
      setRows(data.data || []);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Belgeler yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getDocumentTypes().then(({ data }) => setTypes(data.data || data || [])).catch(() => setTypes([]));
  }, []);

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!fileList.length) {
      message.warning('Lütfen bir dosya seçin.');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', fileList[0].originFileObj || fileList[0]);
      if (values.title) fd.append('title', values.title);
      if (values.competition_document_type_id) fd.append('competition_document_type_id', values.competition_document_type_id);
      if (values.notes) fd.append('notes', values.notes);

      await uploadCompetitionDocument(competitionId, fd);
      message.success('Belge yüklendi.');
      setModalOpen(false);
      form.resetFields();
      setFileList([]);
      load();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Belge yüklenemedi.'));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteDocument(id);
      message.success('Belge silindi.');
      load();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Silinemedi.'));
    }
  };

  const columns = [
    {
      title: 'Belge',
      key: 'title',
      sorter: textSorter('title'),
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.title || r.original_name}</Text>
          {!!r.title && <Text type="secondary" style={{ fontSize: 12 }}>{r.original_name}</Text>}
        </Space>
      ),
    },
    { title: 'Tür', key: 'type', render: (_, r) => r.type?.label || '—' },
    { title: 'Boyut', dataIndex: 'file_size', key: 'file_size', width: 100, render: fileSize },
    {
      title: 'Yükleyen',
      key: 'uploader',
      render: (_, r) => (r.uploader ? `${r.uploader.name} ${r.uploader.surname}` : '—'),
    },
    {
      title: 'Tarih',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      sorter: dateSorter('created_at'),
      render: (v) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    { title: 'Not', dataIndex: 'notes', key: 'notes', render: (v) => v || '—' },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      render: (_, r) => (
        <Space>
          {/* İmzalı ve kısa ömürlü URL - dosya yolu istemciye hiç gitmiyor. */}
          <Button size="small" icon={<DownloadOutlined />} href={r.download_url} target="_blank" rel="noreferrer" />
          <Popconfirm title="Belge silinsin mi?" onConfirm={() => remove(r.id)} okText="Sil" cancelText="Vazgeç" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Yarışma Belgeleri"
      extra={<Button type="primary" icon={<UploadOutlined />} onClick={() => setModalOpen(true)}>Belge Yükle</Button>}
    >
      <Alert
        type="info"
        showIcon
        className="mb-3"
        style={{ marginBottom: 12 }}
        message="Bu belgeler yarışmanın geneline aittir; etap tanımlanmadan da yüklenebilir."
        description="Yönetim evrakı olduğu için velilere gösterilmez. Veliye açılacak belgeler ilgili etabın Belgeler sekmesinden yüklenir."
      />

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        size="small"
        pagination={false}
        locale={{ emptyText: <Empty description="Henüz belge yüklenmemiş." /> }}
      />

      <Modal
        title="Yarışma Belgesi Yükle"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setFileList([]); }}
        onOk={submit}
        confirmLoading={submitting}
        okText="Yükle"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Dosya" required>
            <Upload
              beforeUpload={(f) => { setFileList([f]); return false; }}
              onRemove={() => setFileList([])}
              fileList={fileList}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Dosya Seç</Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12 }}>
              JPG, PNG, WEBP, PDF veya Word · en fazla 30 MB
            </Text>
          </Form.Item>
          <Form.Item name="title" label="Başlık">
            <Input placeholder="Örn. Şartname 2026" maxLength={200} />
          </Form.Item>
          <Form.Item name="competition_document_type_id" label="Tür">
            <Select allowClear placeholder="Seçiniz" options={types.map((t) => ({ value: t.id, label: t.label }))} />
          </Form.Item>
          <Form.Item name="notes" label="Not">
            <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

/**
 * Yarışma notları — her not ayrı kayıt (kim, ne zaman). Etap seçilmesi
 * zorunlu değil; etap yokken de not alınabiliyor.
 */
export const CompetitionNotesTab = ({ competitionId, stages = [] }) => {
  const { user } = useAuthStore();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, record: null });
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCompetitionNotes(competitionId);
      setRows(data.data || []);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Notlar yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => { load(); }, [load]);

  const openModal = (record = null) => {
    setModal({ open: true, record });
    form.setFieldsValue(record
      ? { content: record.content, competition_stage_id: record.competition_stage_id }
      : { content: '', competition_stage_id: undefined });
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      if (modal.record) {
        await updateCompetitionNote(modal.record.id, { content: values.content });
        message.success('Not güncellendi.');
      } else {
        await createCompetitionNote(competitionId, values);
        message.success('Not eklendi.');
      }
      setModal({ open: false, record: null });
      form.resetFields();
      load();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Kaydedilemedi.'));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteCompetitionNote(id);
      message.success('Not silindi.');
      load();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Silinemedi.'));
    }
  };

  // Backend de aynı kuralı uyguluyor (yalnızca yazan ya da süper admin);
  // burası sadece gereksiz butonu göstermemek için.
  const canEdit = (note) =>
    note.created_by === user?.id || ['super_admin', 'developer_admin'].includes(user?.role);

  return (
    <Card
      title="Notlar"
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>Not Ekle</Button>}
    >
      {rows.length === 0 && !loading && (
        <Empty description="Henüz not yok. Yarışma süreciyle ilgili kararları ve yazışmaları buraya not edebilirsiniz." />
      )}

      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        {rows.map((n) => (
          <Card key={n.id} size="small" loading={loading}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
              <Space direction="vertical" size={2} style={{ flex: 1 }}>
                <Space size={6} wrap>
                  <Text strong>{n.author ? `${n.author.name} ${n.author.surname}` : 'Bilinmiyor'}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {n.created_at ? dayjs(n.created_at).format('DD.MM.YYYY HH:mm') : ''}
                  </Text>
                  {!!n.stage && <Text type="secondary" style={{ fontSize: 12 }}>· {n.stage.name}</Text>}
                </Space>
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{n.content}</Paragraph>
              </Space>
              {canEdit(n) && (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openModal(n)} />
                  <Popconfirm title="Not silinsin mi?" onConfirm={() => remove(n.id)} okText="Sil" cancelText="Vazgeç" okButtonProps={{ danger: true }}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              )}
            </Space>
          </Card>
        ))}
      </Space>

      <Modal
        title={modal.record ? 'Notu Düzenle' : 'Not Ekle'}
        open={modal.open}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={submit}
        confirmLoading={submitting}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="content" label="Not" rules={[{ required: true, message: 'Not içeriği zorunlu.' }]}>
            <TextArea autoSize={{ minRows: 4, maxRows: 10 }} maxLength={5000} showCount />
          </Form.Item>
          {/* Etap seçimi yalnızca yeni notta; mevcut notun etabını değiştirmek
              geçmiş kaydı bozar. Etap yoksa alan hiç görünmüyor. */}
          {!modal.record && stages.length > 0 && (
            <Form.Item name="competition_stage_id" label="Etap (isteğe bağlı)">
              <Select allowClear placeholder="Yarışma geneli" options={stages.map((s) => ({ value: s.id, label: s.name }))} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
};
